/**
 * useMessageFlow Hook
 *
 * Reads live RabbitMQ throughput from the Management API (via CCMS.Api.System):
 * cluster-wide publish/deliver/ack rates, per-queue ready/unacked/consumer counts,
 * and non-destructive message peeks (payload + who published it).
 *
 * Note: httpClient.get<T> already wraps the payload in ResponseData<T>, so the
 * generic argument is the bare payload type and the body is read from response.data.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { MessageFlowResult, PeekResult } from '../types';

export function useMessageFlow() {
  const httpClient = useHttpClient();
  const [flow, setFlow] = useState<MessageFlowResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFlow = useCallback(async () => {
    setLoading(true);
    try {
      const response = await httpClient.get<MessageFlowResult>(
        getApiUrl('system', '/api/rabbitmqmanagement/message-flow')
      );
      const data = response.data ?? null;
      setFlow(data);
      setError(data && !data.available ? data.error || 'RabbitMQ Management API unavailable' : null);
    } catch (err: any) {
      if (err?.message?.includes('404') || err?.message?.includes('Not Found')) {
        setError(
          'Message-flow endpoint not available. Start the CCMS.Api.System backend or update the proxy to route /system/* to it.'
        );
      } else {
        setError(err?.message || 'Failed to load message flow');
      }
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  const peekQueue = useCallback(
    async (queueName: string, count = 25): Promise<PeekResult> => {
      const response = await httpClient.get<PeekResult>(
        getApiUrl(
          'system',
          `/api/rabbitmqmanagement/queues/${encodeURIComponent(queueName)}/messages?count=${count}`
        )
      );
      return (
        response.data ?? {
          available: false,
          error: 'Empty response from server',
          queueName,
          queueDepth: 0,
          messages: [],
          timestamp: new Date().toISOString(),
        }
      );
    },
    [httpClient]
  );

  return { flow, loading, error, setError, fetchFlow, peekQueue };
}
